import React, { useState, useEffect } from 'react';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Card,
    CardContent,
    Grid,
    CircularProgress,
    Alert,
    TextField,
    Button,
    Stack,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import {
    Search as SearchIcon,
    Refresh as RefreshIcon,
    Visibility as ViewIcon,
    Delete as DeleteIcon,
    FilterList as FilterIcon,
    Clear as ClearIcon,
    Print as PrintIcon,
    Download as DownloadIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import apiClient from '../../services/apiClient';
import pdfService from '../../services/pdfService';

const DRSList = () => {
    const [loading, setLoading] = useState(false);
    const [drsList, setDrsList] = useState([]);
    const [pagination, setPagination] = useState({
        page: 0,
        rowsPerPage: 15,
        total: 0
    });
    const [filters, setFilters] = useState({
        search: '',
        dateFrom: '',
        dateTo: ''
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [selectedDRS, setSelectedDRS] = useState(null);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [drsDetails, setDrsDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [drsToDelete, setDrsToDelete] = useState(null);

    // Typing in the search box fetches once the user pauses, not per keystroke
    const debouncedSearch = useDebouncedValue(filters.search);

    useEffect(() => {
        fetchDRSList();
    }, [pagination.page, pagination.rowsPerPage, debouncedSearch, filters.dateFrom, filters.dateTo]);

    const fetchDRSList = async () => {
        setLoading(true);
        setError('');

        try {
            const params = {
                page: pagination.page + 1,
                per_page: pagination.rowsPerPage,
                search: filters.search || undefined,
                date_from: filters.dateFrom || undefined,
                date_to: filters.dateTo || undefined
            };

            const response = await apiClient.get('/drs/list', { params });
            
            if (response.data.success) {
                setDrsList(response.data.data.data);
                setPagination(prev => ({
                    ...prev,
                    total: response.data.data.total
                }));
            } else {
                setError(response.data.message || 'Failed to fetch DRS list');
            }
        } catch (error) {
            console.error('Fetch DRS list error:', error);
            setError('Failed to fetch DRS list: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchDRSDetails = async (drsId) => {
        setDetailsLoading(true);
        try {
            const response = await apiClient.get(`/drs/${drsId}`);
            if (response.data.success) {
                setDrsDetails(response.data.data);
            } else {
                setError(response.data.message || 'Failed to fetch DRS details');
            }
        } catch (error) {
            console.error('Fetch DRS details error:', error);
            setError('Failed to fetch DRS details: ' + error.message);
        } finally {
            setDetailsLoading(false);
        }
    };

    const handleViewDRS = async (drs) => {
        setSelectedDRS(drs);
        setViewDialogOpen(true);
        await fetchDRSDetails(drs.id);
    };

    const handleDeleteDRS = (drs) => {
        setDrsToDelete(drs);
        setDeleteDialogOpen(true);
    };

    const confirmDeleteDRS = async () => {
        if (!drsToDelete) return;

        try {
            const response = await apiClient.delete(`/drs/${drsToDelete.id}`);
            
            if (response.data.success) {
                setSuccess(response.data.message);
                fetchDRSList();
                setDeleteDialogOpen(false);
                setDrsToDelete(null);
            } else {
                setError(response.data.message || 'Failed to delete DRS');
            }
        } catch (error) {
            console.error('Delete DRS error:', error);
            setError('Failed to delete DRS: ' + error.message);
        }
    };

    const handlePageChange = (event, newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handleRowsPerPageChange = (event) => {
        setPagination(prev => ({
            ...prev,
            rowsPerPage: parseInt(event.target.value, 10),
            page: 0
        }));
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setPagination(prev => ({ ...prev, page: 0 }));
    };

    const clearFilters = () => {
        setFilters({
            search: '',
            dateFrom: '',
            dateTo: ''
        });
        setPagination(prev => ({ ...prev, page: 0 }));
    };

    const printDRS = async () => {
        if (!drsDetails || !drsDetails.bookings.length) {
            setError('No DRS data to print');
            return;
        }
        try {
            setLoading(true);
            await pdfService.generateAndPrintDeliveryRunSheet(drsDetails);
            setSuccess('Print preview opened successfully');
        } catch (error) {
            setError('Failed to generate print preview: ' + error.message);
            console.error('PDF print error:', error);
        } finally {
            setLoading(false);
        }
    };

    const exportDRSToPdf = async () => {
        if (!drsDetails || !drsDetails.bookings.length) {
            setError('No DRS data to export');
            return;
        }

        try {
            setLoading(true);
            await pdfService.generateAndDownloadDeliveryRunSheet(drsDetails);
            setSuccess('PDF downloaded successfully');
        } catch (error) {
            setError('Failed to generate PDF: ' + error.message);
            console.error('PDF generation error:', error);
        } finally {
            setLoading(false);
        }
    };



    return (
        <DashboardLayout>
            <Box sx={{ p: 3 }}>
                {/* Header */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h4" component="h1" gutterBottom>
                        DRS Management
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        View and manage Delivery Run Sheets (DRS)
                    </Typography>
                </Box>

                {/* Filters */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Filters
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <TextField
                                label="Search"
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                placeholder="DRS No, Staff, Driver, Vehicle, AWB No"
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                label="Date From"
                                type="date"
                                value={filters.dateFrom}
                                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                                fullWidth
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                label="Date To"
                                type="date"
                                value={filters.dateTo}
                                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                                fullWidth
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                <Button
                                    variant="outlined"
                                    onClick={fetchDRSList}
                                    disabled={loading}
                                    startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                                    sx={{ px: 2.5 }}
                                >
                                    Refresh
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={clearFilters}
                                    startIcon={<ClearIcon />}
                                    sx={{ px: 2.5 }}
                                >
                                    Clear
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Messages */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                
                {success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {success}
                    </Alert>
                )}

                {/* DRS List */}
                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>DRS No</TableCell>
                                    <TableCell>Delivery Date</TableCell>
                                    <TableCell>Staff Name</TableCell>
                                    <TableCell>Driver Name</TableCell>
                                    <TableCell>Vehicle No</TableCell>
                                    <TableCell>Total Bookings</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>
                                ) : drsList.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            No DRS found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    drsList.map((drs) => (
                                        <TableRow key={drs.id}>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {drs.drs_no}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {format(new Date(drs.delivery_date), 'dd MMM yyyy')}
                                            </TableCell>
                                            <TableCell>{drs.staff_name}</TableCell>
                                            <TableCell>{drs.driver_name}</TableCell>
                                            <TableCell>{drs.vehicle_no}</TableCell>
                                            <TableCell>{drs.total_bookings}</TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={1}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleViewDRS(drs)}
                                                        color="primary"
                                                    >
                                                        <ViewIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDeleteDRS(drs)}
                                                        color="error"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    
                    <TablePagination
                        component="div"
                        count={pagination.total}
                        page={pagination.page}
                        onPageChange={handlePageChange}
                        rowsPerPage={pagination.rowsPerPage}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        rowsPerPageOptions={[10, 15, 25, 50]}
                    />
                </Paper>

                {/* DRS Details Dialog */}
                <Dialog
                    open={viewDialogOpen}
                    onClose={() => setViewDialogOpen(false)}
                    maxWidth="lg"
                    fullWidth
                >
                    <DialogTitle>
                        DRS Details - {selectedDRS?.drs_no} ({drsDetails?.total_bookings || 0} bookings)
                    </DialogTitle>
                    <DialogContent>
                        {detailsLoading ? (
                            <Box display="flex" justifyContent="center" p={3}>
                                <CircularProgress />
                            </Box>
                        ) : drsDetails ? (
                            <Box>
                                {/* DRS Header Info */}
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={12} md={6}>
                                        <Card>
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom>
                                                    DRS Information
                                                </Typography>
                                                <Typography variant="body2">
                                                    <strong>DRS No:</strong> {drsDetails.drs_header.drs_no}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <strong>Delivery Date:</strong> {format(new Date(drsDetails.delivery_date), 'dd MMM yyyy')}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <strong>Staff Name:</strong> {drsDetails.drs_header.staff_name}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <strong>Driver Name:</strong> {drsDetails.drs_header.driver_name}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <strong>Vehicle No:</strong> {drsDetails.drs_header.vehicle_no}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Card>
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom>
                                                    Additional Information
                                                </Typography>
                                                <Typography variant="body2">
                                                    <strong>Branch:</strong> {drsDetails.drs_header.branch_name || 'N/A'}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <strong>Area/Locator:</strong> {drsDetails.drs_header.area_locator || 'N/A'}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <strong>Total Bookings:</strong> {drsDetails.total_bookings}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>

                                {/* Bookings Table */}
                                <Typography variant="h6" gutterBottom>
                                    Bookings ({drsDetails.bookings.length})
                                </Typography>
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>S.No</TableCell>
                                                <TableCell>AWB No</TableCell>
                                                <TableCell>From</TableCell>
                                                <TableCell>To</TableCell>
                                                <TableCell>Packages</TableCell>
                                                <TableCell>Weight</TableCell>
                                                <TableCell>Amount</TableCell>
                                                <TableCell>Status</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {drsDetails.bookings.map((booking) => (
                                                <TableRow key={booking.awb_no}>
                                                    <TableCell>{booking.si_no}</TableCell>
                                                    <TableCell>{booking.awb_no}</TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {booking.from_customer}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {booking.from_city}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {booking.to_customer}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {booking.to_city}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>{booking.no_of_packages}</TableCell>
                                                    <TableCell>{booking.weight} kg</TableCell>
                                                    <TableCell>₹{booking.total_amount}</TableCell>
                                                    <TableCell>
                                                        {booking.delivery_status}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        ) : (
                            <Typography>No details available</Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="outlined"
                                onClick={printDRS}
                                disabled={loading || !drsDetails}
                                startIcon={loading ? <CircularProgress size={20} /> : <PrintIcon />}
                            >
                                Print
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={exportDRSToPdf}
                                disabled={loading || !drsDetails}
                                startIcon={loading ? <CircularProgress size={20} /> : <DownloadIcon />}
                            >
                                Export PDF
                            </Button>
                            <Button onClick={() => setViewDialogOpen(false)}>
                                Close
                            </Button>
                        </Stack>
                    </DialogActions>
                </Dialog>

                {/* Delete Confirmation Dialog */}
                <Dialog
                    open={deleteDialogOpen}
                    onClose={() => setDeleteDialogOpen(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        Confirm Delete DRS
                    </DialogTitle>
                    <DialogContent>
                        <Typography>
                            Are you sure you want to delete DRS <strong>{drsToDelete?.drs_no}</strong>?
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            This action cannot be undone. All associated delivery records will also be deleted.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={confirmDeleteDRS} color="error" variant="contained">
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </DashboardLayout>
    );
};

export default DRSList;
